import { useState, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { useFeed } from "../hooks/useFeed";
import { PanelHeader } from "./PanelHeader";
import { SnowIcon, BoltIcon, SunIcon } from "./Icons";
import type { CampusAsset, ControlLogEntry } from "@kmitl/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

/* The three assets whose glyph has an emoji presentation variant (❄ ⚡ ☀)
   use a drawn mark instead — see components/Icons.tsx. The rest are
   purely geometric characters with no emoji form and are left as
   typographic notation. */
const TYPE_ICON: Record<CampusAsset["type"], ReactNode> = {
  gate:                "⬡",
  "light-zone":        "◉",
  "ac-unit":           <SnowIcon size={13} />,
  cctv:                "◈",
  pump:                "▾",
  alarm:               "▲",
  elevator:            "▣",
  transformer:         "⌂",
  generator:           <BoltIcon size={13} />,
  "solar-inverter":    <SunIcon size={13} />,
  "capacitor-bank":    "⊞",
  mdb:                 "⊟",
  "distribution-board": "⊟",
  ups:                 "⊠",
};

const STATE_COLOR: Record<string, string> = {
  on:         "var(--good)",
  off:        "var(--text-3)",
  auto:       "var(--data)",
  fault:      "var(--bad)",
  open:       "var(--good)",
  closed:     "var(--warn)",
  restricted: "var(--warn)",
  // Electrical states
  online:     "var(--good)",
  offline:    "var(--text-3)",
  standby:    "var(--data)",
  running:    "var(--good)",
  engaged:    "var(--good)",
  exporting:  "var(--good)",
};

// Electrical assets carry descriptive states ("online · 72% load").
// Resolve color/label by the leading token so they still render meaningfully.
function stateColor(state: string): string {
  return STATE_COLOR[state] ?? STATE_COLOR[state.split(" ")[0]] ?? "var(--text-3)";
}

// Mirror of the server's state-cycle logic so the chip can flip instantly
// before the round-trip resolves. Must stay in sync with the API adapter.
const STATE_CYCLE: Partial<Record<CampusAsset["type"], string[]>> = {
  gate:             ["open", "restricted", "closed"],
  "ac-unit":        ["on", "auto", "off"],
  "light-zone":     ["on", "auto", "off"],
  pump:             ["on", "auto", "off"],
  generator:        ["standby", "running"],
  "solar-inverter": ["online", "offline"],
  "capacitor-bank": ["engaged", "standby"],
};

// Electrical states may carry a descriptive suffix ("online · 72% load").
// Cycle on the leading token, but preserve the original suffix on the result
// so the predicted label still reads meaningfully until the server reconciles.
function nextState(current: string, type: CampusAsset["type"]): string {
  const head = current.split(" ")[0];
  const cycle = STATE_CYCLE[type] ?? ["on", "off"];
  const idx = cycle.indexOf(head);
  const nextHead = cycle[(idx + 1) % cycle.length];
  const suffixStart = current.indexOf(" ");
  return suffixStart === -1 ? nextHead : nextHead + current.slice(suffixStart);
}

const FLASH_MS = 600;

const ZONE_ORDER = [
  "Electrical", "Perimeter", "Central", "Engineering", "IT Faculty",
  "Science", "Athletic", "Residential", "Grounds", "Roads", "Parking",
];

function groupByZone(assets: CampusAsset[]): Map<string, CampusAsset[]> {
  const map = new Map<string, CampusAsset[]>();
  for (const a of assets) {
    const g = map.get(a.zone) ?? [];
    g.push(a);
    map.set(a.zone, g);
  }
  return map;
}

function fmtTs(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Bangkok",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function CampusControlPanel() {
  const feed = useFeed<CampusAsset>(`${API_BASE}/api/campus/controls`, 5_000);
  const logFeed = useFeed<ControlLogEntry>(`${API_BASE}/api/campus/controls/log`, 5_000);

  const [pending, setPending] = useState<Set<string>>(new Set());
  // Client-predicted state overrides, keyed by asset id. Cleared once the
  // server feed reconciles to the same value (or on rollback).
  const [optimistic, setOptimistic] = useState<Record<string, string>>({});
  const [flashing, setFlashing] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [readonlyNotice, setReadonlyNotice] = useState<string | null>(null);

  const toggle = useCallback(async (asset: CampusAsset) => {
    if (!asset.canControl) {
      // Still interactive — explain why it won't cycle instead of a dead button.
      setReadonlyNotice(
        `${asset.name.en}: read-only telemetry · BMS write-back pending`,
      );
      setFlashing((f) => new Set(f).add(asset.id));
      setTimeout(() => {
        setFlashing((f) => { const n = new Set(f); n.delete(asset.id); return n; });
        setReadonlyNotice(null);
      }, FLASH_MS * 4);
      return;
    }
    if (pending.has(asset.id)) return;

    const prevState = optimistic[asset.id] ?? asset.state;
    const predicted = nextState(prevState, asset.type);

    // 1. Flip instantly — zero perceived latency.
    setOptimistic((o) => ({ ...o, [asset.id]: predicted }));
    setFailed((f) => { const n = new Set(f); n.delete(asset.id); return n; });
    setFlashing((f) => new Set(f).add(asset.id));
    setTimeout(() => {
      setFlashing((f) => { const n = new Set(f); n.delete(asset.id); return n; });
    }, FLASH_MS);
    setPending((p) => new Set(p).add(asset.id));

    try {
      const res = await fetch(`${API_BASE}/api/campus/controls/${asset.id}/set`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // 2a. Success — let the feed become the source of truth, keep audit fresh.
      feed.refetch();
      logFeed.refetch();
    } catch {
      // 2b. Failure — roll back to the previous state, show error affordance.
      setOptimistic((o) => ({ ...o, [asset.id]: prevState }));
      setFailed((f) => new Set(f).add(asset.id));
      setTimeout(() => {
        setFailed((f) => { const n = new Set(f); n.delete(asset.id); return n; });
      }, FLASH_MS * 3);
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(asset.id); return n; });
    }
  }, [pending, optimistic, feed, logFeed]);

  // Reconcile: once the server feed reports the predicted value, drop the
  // local override so the feed is the single source of truth again.
  const reconcile = useCallback((assets: CampusAsset[]) => {
    setOptimistic((o) => {
      let changed = false;
      const next: Record<string, string> = {};
      for (const [id, predicted] of Object.entries(o)) {
        const live = assets.find((a) => a.id === id);
        if (live && live.state === predicted) { changed = true; continue; }
        next[id] = predicted;
      }
      return changed ? next : o;
    });
  }, []);

  useEffect(() => { reconcile(feed.data); }, [feed.data, reconcile]);

  const grouped = groupByZone(feed.data);
  const zones = ZONE_ORDER.filter((z) => grouped.has(z));
  const log = logFeed.data;

  return (
    <div className="control-panel left-section">
      <PanelHeader
        title="GOD'S MODE — CAMPUS CONTROL"
        ageMinutes={feed.ageMinutes}
        fallbackTier={feed.fallbackTier === "loading" ? undefined : feed.fallbackTier}
        source="kmitl-bms"
      />
      <p className="control-notice mono">
        Scenario model · server-authoritative · BMS integration pending
      </p>
      {readonlyNotice && (
        <p className="control-notice mono" role="status" style={{ color: "var(--warn)" }}>
          {readonlyNotice}
        </p>
      )}

      {zones.map((zone) => {
        const assets = grouped.get(zone) ?? [];
        return (
          <div key={zone} className="control-zone">
            <span className="control-zone-label mono">{zone.toUpperCase()}</span>
            <div className="control-asset-list">
              {assets.map((a) => {
                // Predicted override wins until the server feed reconciles.
                const shownState = optimistic[a.id] ?? a.state;
                const color = stateColor(shownState);
                const isPending = pending.has(a.id);
                const isFlashing = flashing.has(a.id);
                const isFailed = failed.has(a.id);
                const cls = [
                  "control-asset",
                  a.canControl ? "control-asset--ctrl" : "control-asset--readonly",
                  `priority-${a.priority}`,
                  isPending ? "control-asset--pending" : "",
                  isFlashing ? "control-asset--flash" : "",
                  isFailed ? "control-asset--failed" : "",
                ].filter(Boolean).join(" ");
                return (
                  <button
                    key={a.id}
                    className={cls}
                    onClick={() => toggle(a)}
                    disabled={isPending}
                    title={`${a.name.en} · ${shownState}${!a.canControl ? " · read-only (tap for why)" : " · click to cycle"}`}
                  >
                    <span className="control-asset-icon" style={{ color }}>
                      {TYPE_ICON[a.type]}
                    </span>
                    <span className="control-asset-name mono">{a.name.en}</span>
                    <span className="control-asset-state mono" style={{ color }}>
                      {isFailed ? "ERR" : shownState.toUpperCase()}
                    </span>
                    {a.priority === "critical" && (
                      <span className="control-priority-dot" title="Critical system" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ── Audit log ── */}
      {log.length > 0 && (
        <div className="control-log">
          <span className="control-log-label mono eyebrow">Action log</span>
          <div className="control-log-entries">
            {log.slice(0, 12).map((e) => (
              <div key={e.id} className="control-log-row mono">
                <span className="control-log-ts">{fmtTs(e.ts)}</span>
                <span className="control-log-name">{e.assetName}</span>
                <span className="control-log-arrow">
                  <span style={{ color: stateColor(e.fromState) }}>
                    {e.fromState.toUpperCase()}
                  </span>
                  {" → "}
                  <span style={{ color: stateColor(e.toState) }}>
                    {e.toState.toUpperCase()}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
